import React, { useState, useRef } from 'react';
import './ImageTransformer.css';

const ImageTransformer = ({ onTransformComplete }) => {
  const [uploadedImageBase64, setUploadedImageBase64] = useState(null);
  const [generatedImageUrl, setGeneratedImageUrl] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const uploadInputRef = useRef(null);

  const API_URL = '/api/transform-image';
  
  const handleUploadClick = () => {
    uploadInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setUploadedImageBase64(event.target.result);
      setGeneratedImageUrl(null);
      setError(null);
    };
    reader.readAsDataURL(file);
  };

  const handleGenerate = async () => {
    if (!uploadedImageBase64) {
      setError('Please upload an image first!');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          imageData: uploadedImageBase64
        })
      });

      // Check if response is HTML (API not available)
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('text/html')) {
        throw new Error('API endpoint not available. Please set up the backend server.');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Server error' }));
        throw new Error(errorData.error || `Server error: ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.imageUrl) {
        setGeneratedImageUrl(data.imageUrl);
        // Notify parent component that transformation is complete
        if (onTransformComplete) {
          onTransformComplete();
        }
      } else {
        throw new Error('No image returned from API');
      }
    } catch (err) {
      console.error('Image Transformation Error:', err);
      setError(`Error: ${err.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = async () => {
    if (!generatedImageUrl) return;

    try {
      const response = await fetch(generatedImageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = 'lemondick-image.png';
      link.href = url;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Failed to download image');
    }
  };

  const handleCopyCA = async () => {
    const caAddress = 'hJmXKxXSBznzmRZ4BawSZ4z51u1dsorkvAt4aLXpump';
    try {
      await navigator.clipboard.writeText(caAddress);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    } catch (error) {
      console.error('Failed to copy CA');
    }
  };

  return (
    <div className="transformer-container">
      {showToast && (
        <div className="toast-notification">
          CA copied to clipboard!
        </div>
      )}
      <div className="ca-text" onClick={handleCopyCA}>
        CA: <span className="ca-address">lemondick</span>
      </div>
      <div className="transformer-card">
        <div className="upload-section">
          <input
            type="file"
            ref={uploadInputRef}
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <button onClick={handleUploadClick} className="upload-btn">
            Upload Image
          </button>
        </div>

        {uploadedImageBase64 && (
          <div className="image-preview">
            <h3>Original</h3>
            <img src={uploadedImageBase64} alt="Original" />
          </div>
        )}

        <div className="result-area">
          {isGenerating ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p className="loading-text">Transforming your image...</p>
            </div>
          ) : error ? (
            <p className="error-text">{error}</p>
          ) : generatedImageUrl ? (
            <div className="image-result">
              <h3>Transformed</h3>
              <img src={generatedImageUrl} alt="Transformed" />
            </div>
          ) : (
            <p className="placeholder-text">Transformed image will appear here</p>
          )}
        </div>

        <div className="action-buttons">
          {uploadedImageBase64 && (
            <button
              onClick={handleGenerate}
              className="transform-btn"
              disabled={isGenerating}
            >
              {isGenerating ? 'Transforming...' : 'Transform'}
            </button>
          )}
          {generatedImageUrl && (
            <button onClick={handleDownload} className="download-btn">
              Download Image
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageTransformer;
